import {
	bulkSpamPanelSessions,
	importPanelSenderSessionFile,
	uploadPanelSenderSessionsZip,
} from '@/api/sender-sessions';
import {
	fetchSenderAutoCampaign,
	startSenderAutoCampaign,
	stopSenderAutoCampaign,
	upsertSenderAutoCampaign,
	type SenderAutoCampaignView,
} from '@/api/sender-auto-campaign';
import { fetchSenderPresets, type SenderRunPreset } from '@/api/sender-presets';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { $features } from '@/entities/features-entity';
import { resolveSenderActionError } from '@/lib/resolve-sender-action-error';
import { senderRoutes } from '@/lib/sender-routes';
import {
	formatBulkSpamOutcome,
	shouldOpenConsoleAfterBulkSpam,
} from '@/pages/sender/sessions-actions';
import SenderEmptyState from '@/pages/sender/components/sender-empty-state';
import SenderLoadError from '@/pages/sender/components/sender-load-error';
import SenderMetricGrid from '@/pages/sender/components/sender-metric-grid';
import SenderSegment from '@/pages/sender/components/sender-segment';
import SenderSelectRow from '@/pages/sender/components/sender-select-row';
import SenderUploadZone from '@/pages/sender/components/sender-upload-zone';
import { senderSurfaceClass } from '@/pages/sender/components/sender-surface';
import { cn } from '@/lib/utils';
import {
	canStartAutoCampaign,
	isAutoQueueDirty,
	moveAutoPreset,
	toggleAutoPreset,
} from '@/pages/sender/auto-queue';
import { formatScenarioMeaning } from '@/pages/sender/scenario-meaning';
import { SenderScreen, SENDER_ADMIN_DISABLED, senderPrimaryButtonClassName } from '@/pages/sender/sender-screen';
import { useUnit } from 'effector-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';

type SendMode = 'batch' | 'one' | 'auto';

type ImportRow = {
	key: string;
	name: string;
	ok: boolean;
	detail: string;
	sessionId?: string;
};

function resolveSendMode(raw: string | null, autoEnabled: boolean): SendMode {
	if (raw === 'one') return 'one';
	if (raw === 'auto' && autoEnabled) return 'auto';
	return 'batch';
}

function useSendMode(): SendMode {
	const [params, setParams] = useSearchParams();
	const { senderAutoCampaignEnabled } = useUnit($features);
	const rawMode = params.get('mode');
	const mode = resolveSendMode(rawMode, senderAutoCampaignEnabled);

	useEffect(() => {
		if (rawMode == null) return;
		const next = resolveSendMode(rawMode, senderAutoCampaignEnabled);
		if (rawMode !== next) {
			setParams({ mode: next }, { replace: true });
		}
	}, [rawMode, senderAutoCampaignEnabled, setParams]);

	return mode;
}

export default function SenderSendPage() {
	const mode = useSendMode();
	const { senderPresetsEnabled } = useUnit($features);
	if (mode === 'one') return <OneMode />;
	if (mode === 'auto') return <AutoMode presetsEnabled={senderPresetsEnabled} />;
	return <BatchMode presetsEnabled={senderPresetsEnabled} />;
}

function ModeSwitch() {
	const [params, setParams] = useSearchParams();
	const { senderAutoCampaignEnabled } = useUnit($features);
	const mode = resolveSendMode(params.get('mode'), senderAutoCampaignEnabled);

	function setMode(next: SendMode) {
		setParams({ mode: next }, { replace: true });
	}

	const options = [
		{ value: 'batch' as const, label: 'Пачка' },
		{ value: 'one' as const, label: 'Один' },
		...(senderAutoCampaignEnabled ? [{ value: 'auto' as const, label: 'Авто' }] : []),
	];
	return (
		<SenderSegment options={options} value={mode} onChange={setMode} />
	);
}

function OneMode() {
	const navigate = useNavigate();
	return (
		<SenderScreen
			title='Рассылка'
			footer={
				<Button
					type='button'
					className={senderPrimaryButtonClassName()}
					onClick={() => navigate(senderRoutes.sendOne)}
				>
					Дальше
				</Button>
			}
		>
			<ModeSwitch />
			<p className='text-[15px]'>Вход по номеру и один запуск.</p>
		</SenderScreen>
	);
}

function BatchMode({ presetsEnabled }: { presetsEnabled: boolean }) {
	const navigate = useNavigate();
	const inputRef = useRef<HTMLInputElement>(null);
	const [rows, setRows] = useState<ImportRow[]>([]);
	const [presets, setPresets] = useState<SenderRunPreset[]>([]);
	const [presetId, setPresetId] = useState<string | null>(null);
	const [loadingPresets, setLoadingPresets] = useState(presetsEnabled);
	const [presetError, setPresetError] = useState(false);
	const [presetNonce, setPresetNonce] = useState(0);
	const [uploading, setUploading] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [starting, setStarting] = useState(false);

	useEffect(() => {
		if (!presetsEnabled) {
			setLoadingPresets(false);
			setPresetError(false);
			return;
		}
		let cancelled = false;
		setLoadingPresets(true);
		setPresetError(false);
		void fetchSenderPresets()
			.then(items => {
				if (cancelled) return;
				setPresets(items);
				setPresetId(current => current ?? items[0]?.id ?? null);
			})
			.catch(() => {
				if (cancelled) return;
				setPresets([]);
				setPresetError(true);
			})
			.finally(() => {
				if (!cancelled) setLoadingPresets(false);
			});
		return () => {
			cancelled = true;
		};
	}, [presetsEnabled, presetNonce]);

	const importedIds = rows.filter(row => row.ok && row.sessionId).map(row => row.sessionId!);
	const selected = presets.find(preset => preset.id === presetId) ?? null;
	const canLaunch = importedIds.length > 0 && Boolean(selected);

	async function handleFiles(list: FileList | null) {
		if (!list?.length) return;
		const files = [...list];
		setUploading(true);
		const next: ImportRow[] = [];
		try {
			for (const file of files) {
				const isZip = file.name.toLowerCase().endsWith('.zip') || file.type === 'application/zip';
				if (isZip) {
					try {
						const result = await uploadPanelSenderSessionsZip(file);
						for (const row of result.imported) {
							next.push({
								key: row.sessionId,
								name: row.fileName || file.name,
								ok: true,
								detail: 'Готово',
								sessionId: row.sessionId,
							});
						}
						for (const row of result.errors) {
							next.push({
								key: `${file.name}-${row.fileName}-${row.reason}`,
								name: row.fileName,
								ok: false,
								detail: row.reason,
							});
						}
					} catch (error) {
						next.push({
							key: file.name,
							name: file.name,
							ok: false,
							detail: resolveSenderActionError(error, 'Не удалось загрузить'),
						});
					}
				} else {
					try {
						const row = await importPanelSenderSessionFile(file);
						next.push({
							key: row.sessionId,
							name: row.fileName || file.name,
							ok: true,
							detail: 'Готово',
							sessionId: row.sessionId,
						});
					} catch (error) {
						next.push({
							key: file.name,
							name: file.name,
							ok: false,
							detail: resolveSenderActionError(error, 'Не удалось загрузить'),
						});
					}
				}
			}
			setRows(next);
		} finally {
			setUploading(false);
			if (inputRef.current) inputRef.current.value = '';
		}
	}

	async function confirmLaunch() {
		if (!selected || importedIds.length === 0) return;
		setStarting(true);
		try {
			const result = await bulkSpamPanelSessions({ sessionIds: importedIds, presetId: selected.id });
			const notice = formatBulkSpamOutcome(result);
			setConfirmOpen(false);
			if (shouldOpenConsoleAfterBulkSpam(result.created.length)) {
				toast.success(notice);
				navigate(senderRoutes.console, { state: { from: senderRoutes.send } });
			} else {
				toast.warning(notice);
			}
		} catch (error) {
			toast.error(resolveSenderActionError(error, 'Не удалось запустить'));
		} finally {
			setStarting(false);
		}
	}

	const launchFooter =
		presetsEnabled && !presetError ? (
			<Button
				type='button'
				className={senderPrimaryButtonClassName()}
				disabled={!canLaunch || starting || loadingPresets}
				onClick={() => setConfirmOpen(true)}
			>
				Запустить {importedIds.length}
			</Button>
		) : null;

	return (
		<SenderScreen title='Рассылка' footer={launchFooter}>
			<ModeSwitch />
			<input
				ref={inputRef}
				type='file'
				accept='.zip,.session,application/zip'
				multiple
				className='hidden'
				onChange={event => void handleFiles(event.target.files)}
			/>
			<SenderUploadZone
				label={uploading ? 'Загрузка…' : 'Архив или несколько файлов сессии'}
				disabled={uploading}
				busy={uploading}
				onPick={() => inputRef.current?.click()}
			>
				{rows.length > 0 ? (
					<ul className='flex flex-col gap-2'>
						{rows.map(row => (
							<li key={row.key} className={cn(senderSurfaceClass, 'px-3 py-2')}>
								<span className='block text-[15px] font-medium'>{row.name}</span>
								<span className={cn('block text-[13px]', row.ok ? 'text-chart-2' : 'text-destructive')}>
									{row.ok ? 'Готово' : row.detail}
								</span>
							</li>
						))}
					</ul>
				) : null}
			</SenderUploadZone>
			{!presetsEnabled ? (
				<p className='text-[15px]'>{SENDER_ADMIN_DISABLED}</p>
			) : loadingPresets ? (
				<div className='flex justify-center py-6'>
					<Spinner />
				</div>
			) : presetError ? (
				<SenderLoadError onRetry={() => setPresetNonce(value => value + 1)} />
			) : presets.length === 0 ? (
				<SenderEmptyState
					message='Сценариев нет'
					action={
						<Button type='button' className='min-h-11' onClick={() => navigate(senderRoutes.scenarioNew)}>
							Создать сценарий
						</Button>
					}
				/>
			) : (
				<div className='flex flex-col gap-2'>
					<p className='text-[13px] text-muted-foreground'>Сценарий</p>
					{presets.map(preset => (
						<SenderSelectRow
							key={preset.id}
							title={preset.name}
							meaning={formatScenarioMeaning(preset)}
							selected={preset.id === presetId}
							onSelect={() => setPresetId(preset.id)}
						/>
					))}
				</div>
			)}
			<Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Запустить</DialogTitle>
					</DialogHeader>
					<div className='flex flex-col gap-1 text-[15px]'>
						<p>Сессий: {importedIds.length}</p>
						<p>Сценарий: {selected?.name}</p>
						<p>
							{selected?.deleteAccountAfterRun
								? 'Сессия удалится; аккаунт Telegram — через my.telegram.org'
								: 'Сессия останется'}
						</p>
					</div>
					<DialogFooter className='flex-col gap-2 sm:flex-col'>
						<Button type='button' className='min-h-11' disabled={starting} onClick={() => void confirmLaunch()}>
							{starting ? <Spinner className='size-4' /> : 'Запустить'}
						</Button>
						<Button type='button' variant='outline' className='min-h-11' onClick={() => setConfirmOpen(false)}>
							Отмена
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</SenderScreen>
	);
}

function AutoMode({ presetsEnabled }: { presetsEnabled: boolean }) {
	const navigate = useNavigate();
	const [campaign, setCampaign] = useState<SenderAutoCampaignView | null>(null);
	const [savedIds, setSavedIds] = useState<string[]>([]);
	const [draftIds, setDraftIds] = useState<string[]>([]);
	const [presets, setPresets] = useState<SenderRunPreset[]>([]);
	const [loading, setLoading] = useState(presetsEnabled);
	const [error, setError] = useState(false);
	const [saving, setSaving] = useState(false);
	const [emptyOpen, setEmptyOpen] = useState(false);

	const load = useCallback(async () => {
		if (!presetsEnabled) {
			setLoading(false);
			setError(false);
			return;
		}
		setLoading(true);
		setError(false);
		try {
			const [nextCampaign, nextPresets] = await Promise.all([
				fetchSenderAutoCampaign(),
				fetchSenderPresets(),
			]);
			setCampaign(nextCampaign);
			setSavedIds(nextCampaign.presetIds);
			setDraftIds(nextCampaign.presetIds);
			setPresets(nextPresets);
		} catch {
			setCampaign(null);
			setError(true);
		} finally {
			setLoading(false);
		}
	}, [presetsEnabled]);

	useEffect(() => {
		void load();
	}, [load]);

	const dirty = isAutoQueueDirty(savedIds, draftIds);
	const running = campaign?.status === 'running';

	async function onToggle(next: boolean) {
		if (next && !canStartAutoCampaign(savedIds)) {
			setEmptyOpen(true);
			return;
		}
		if (!next) {
			try {
				const view = await stopSenderAutoCampaign();
				setCampaign(view);
			} catch (error) {
				toast.error(resolveSenderActionError(error, 'Не удалось выключить'));
			}
			return;
		}
		try {
			const view = await startSenderAutoCampaign();
			setCampaign(view);
		} catch (error) {
			toast.error(resolveSenderActionError(error, 'Не удалось включить'));
		}
	}

	async function saveQueue() {
		setSaving(true);
		try {
			const view = await upsertSenderAutoCampaign({ presetIds: draftIds });
			setCampaign(view);
			setSavedIds(view.presetIds);
			setDraftIds(view.presetIds);
			toast.success('Очередь сохранена');
		} catch (error) {
			toast.error(resolveSenderActionError(error, 'Не удалось сохранить'));
		} finally {
			setSaving(false);
		}
	}

	const rows = useMemo(() => {
		const byId = new Map(presets.map(preset => [preset.id, preset]));
		const checked = draftIds.map(id => byId.get(id)).filter((preset): preset is SenderRunPreset => Boolean(preset));
		const rest = presets.filter(preset => !draftIds.includes(preset.id));
		return [...checked, ...rest];
	}, [presets, draftIds]);

	const saveFooter =
		presetsEnabled && !loading && !error ? (
			<Button
				type='button'
				className={senderPrimaryButtonClassName()}
				disabled={!dirty || saving}
				onClick={() => void saveQueue()}
			>
				{saving ? <Spinner className='size-4' /> : 'Сохранить очередь'}
			</Button>
		) : null;

	return (
		<SenderScreen title='Рассылка' footer={saveFooter}>
			<ModeSwitch />
			{!presetsEnabled ? (
				<p className='text-[15px]'>{SENDER_ADMIN_DISABLED}</p>
			) : loading ? (
				<div className='flex justify-center py-8'>
					<Spinner />
				</div>
			) : error ? (
				<SenderLoadError onRetry={() => void load()} />
			) : (
		<div className='flex flex-col gap-4'>
			<div className={cn(senderSurfaceClass, 'flex min-h-11 items-center justify-between gap-3 px-4 py-3')}>
				<p className='text-[20px] font-medium'>{running ? 'Включена' : 'Выключена'}</p>
				<Switch checked={running} onCheckedChange={checked => void onToggle(checked)} />
			</div>
			<SenderMetricGrid
				columns={4}
				metrics={[
					{ label: 'Ожидаем', value: String(campaign?.queuePending ?? 0) },
					{ label: 'Идёт', value: String(campaign?.queueRunning ?? 0), tone: 'active' },
					{ label: 'Готово', value: String(campaign?.queueDone ?? 0), tone: 'ok' },
					{ label: 'Ошибка', value: String(campaign?.queueFailed ?? 0), tone: 'bad' },
				]}
			/>
			{presets.length === 0 ? (
				<div className='flex flex-col gap-2'>
					<p className='text-[15px]'>Сценариев нет</p>
					<Button type='button' className='min-h-11 w-fit' onClick={() => navigate(senderRoutes.scenarioNew)}>
						Создать сценарий
					</Button>
				</div>
			) : (
				<ul className='flex flex-col gap-2'>
					{rows.map(preset => {
						const checked = draftIds.includes(preset.id);
						return (
							<li key={preset.id} className={cn(senderSurfaceClass, 'px-3 py-2')}>
								<label className='flex min-h-11 items-start gap-2 text-[15px]'>
									<input
										type='checkbox'
										className='mt-1'
										checked={checked}
										onChange={event => setDraftIds(toggleAutoPreset(draftIds, preset.id, event.target.checked))}
									/>
									<span>
										<span className='block font-medium'>{preset.name}</span>
										<span className='block text-[13px] text-muted-foreground'>{formatScenarioMeaning(preset)}</span>
									</span>
								</label>
								{checked ? (
									<div className='flex gap-2'>
										<Button
											type='button'
											variant='outline'
											className='min-h-11'
											onClick={() => setDraftIds(moveAutoPreset(draftIds, preset.id, -1))}
										>
											Выше
										</Button>
										<Button
											type='button'
											variant='outline'
											className='min-h-11'
											onClick={() => setDraftIds(moveAutoPreset(draftIds, preset.id, 1))}
										>
											Ниже
										</Button>
									</div>
								) : null}
							</li>
						);
					})}
				</ul>
			)}
			<Dialog open={emptyOpen} onOpenChange={setEmptyOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Сначала выберите сценарии</DialogTitle>
					</DialogHeader>
					<DialogFooter>
						<Button type='button' className='min-h-11' onClick={() => setEmptyOpen(false)}>
							Понятно
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
			)}
		</SenderScreen>
	);
}

